import { navLinks } from '../layout/home-layout/links';
import { DASHBOARD } from '../constants/route-paths';
import idx from 'idx';
import { isEmpty } from 'lodash';

/**
 * Get the smart navigation path based on user permissions and available menu items
 * @param {Object} data - Object containing user and project categories data
 * @param {Object} data.user - User data from Redux state/ login response
 * @param {Array} data.projectCategories - Project categories from Redux state/ login response
 * @returns {string|null} - Navigation path or null if data not available
 */
export const getSmartNavigationPath = (data) => {
    // Extract user and project categories from data
    const user = idx(data, _ => _.user) || {};
    const projectCategories = idx(data, _ => _.projectCategories) || [];
    // Wait for all required data to be available
    // If data is not available yet, return null
    if (isEmpty(user) || isEmpty(projectCategories)) return null;

    // Helper function to safely extract nested properties
    const safeExtract = (obj, paths) => {
        for (const path of paths) {
            const value = idx(obj, path);
            if (value) return value;
        }
        return {};
    };

    // Get available navigation links based on user permissions
    const availableNavLinks = navLinks({
        projectCategories: projectCategories,
        user: safeExtract(user, [_ => _.userData, _ => _]),
        entitlements: safeExtract(data, [_ => _.user.entitlements, _ => _.user.userData.entitlements, _ => _.entitlements]),
        permissions: safeExtract(user, [_ => _.userData.permissions, _ => _.permissions])
    });

    // Filter links that user has access to (permission > 0, not disabled, and has path or accessible subItems)
    const accessibleLinks = [];

    availableNavLinks.forEach(link => {
        const hasPermission = idx(link, _ => _.permission) > 0;
        const subListItems = idx(link, _ => _.subListItems);

        if (hasPermission) {
            const navigatePath = idx(link, _ => _.path);
            // Check if it has a direct path
            if (navigatePath) {
                accessibleLinks.push({
                    ...link,
                    navigatePath
                });
            }
            // Or if it has accessible subListItems
            else if (subListItems && subListItems.length > 0) {
                // Find first accessible sub-item 
                const accessibleSubItem = subListItems.find(subItem => {
                    const subHasPermission = idx(subItem, _ => _.permission) > 0;
                    const subIsNotDisabled = idx(subItem, _ => _.disabled) !== true;
                    const subHasPath = idx(subItem, _ => _.path);
                    return subHasPermission && subIsNotDisabled && subHasPath;
                });

                if (accessibleSubItem) {
                    accessibleLinks.push({
                        ...link,
                        navigatePath: idx(accessibleSubItem, _ => _.path),
                        selectedSubItem: accessibleSubItem
                    });
                }
            }
        }
    });

    // If no accessible links found, redirect to dashboard
    if (accessibleLinks.length === 0) return `/${DASHBOARD}`;

    // Step 1: Look for Projects in the accessible links
    const projectsLink = accessibleLinks.find(link => idx(link, _ => _.name) === 'Projects');

    // If Projects link is found and has a navigatePath, return it
    // This assumes 'Projects' is the name of the link for project management
    if (projectsLink && idx(projectsLink, _ => _.navigatePath)) return idx(projectsLink, _ => _.navigatePath);

    // Step 2: If no projects, find first non-dashboard link
    const nonDashboardLinks = accessibleLinks.filter(link => idx(link, _ => _.name) !== 'Dashboard');

    if (nonDashboardLinks.length > 0) {
        const firstNonDashboardLink = nonDashboardLinks[0];
        return idx(firstNonDashboardLink, _ => _.navigatePath);
    }

    // Step 3: If only dashboard is available, navigate to dashboard
    const dashboardLink = accessibleLinks.find(link => idx(link, _ => _.name) === 'Dashboard');
    if (dashboardLink && idx(dashboardLink, _ => _.navigatePath)) return idx(dashboardLink, _ => _.navigatePath);

    // Fallback to dashboard
    return `/${DASHBOARD}`;
};